/**
 * Protected Route Component
 * AD-20251113-SUPABASE-AUTH-VITE: Updated to use useAuth hook instead of duplicate logic
 *
 * Wraps routes that require authentication. Redirects to login if user is not authenticated.
 */

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export default function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { user, loading, session } = useAuth();
  const location = useLocation();

  // Per Supabase docs: wait for initial session check to complete
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary mb-4" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // Per Supabase best practices: check both user and session for stability
  // Only redirect if we have no user AND no session (fully unauthenticated)
  if (!user || !session) {
    // Redirect to login with return URL
    return <Navigate to={`/account/login?redirect=${encodeURIComponent(location.pathname)}`} replace />;
  }

  return <>{children}</>;
}
